import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { BookOpen, FileDown, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const catColors = {
    alerte: 'bg-red-500', decision: 'bg-amber-500', intervention: 'bg-blue-500',
    logistique: 'bg-purple-500', communication: 'bg-green-500', general: 'bg-zinc-500'
};
const catLabels = {
    alerte: 'Alerte', decision: 'Decision', intervention: 'Intervention',
    logistique: 'Logistique', communication: 'Communication', general: 'General'
};

const MainCouranteCard = ({ mainCourante, token, isChef, onRefresh }) => {
    const [mcForm, setMcForm] = useState({ event_time: '', description: '', category: 'general' });
    const [showMcForm, setShowMcForm] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const handleAddMcEvent = async (e) => {
        e.preventDefault();
        if (!mcForm.description.trim()) return;
        try {
            const payload = {
                event_time: mcForm.event_time || new Date().toISOString(),
                description: mcForm.description.trim(),
                category: mcForm.category
            };
            await axios.post(`${API}/main-courante`, payload, { headers });
            toast.success('Evenement ajoute');
            setMcForm({ event_time: '', description: '', category: 'general' });
            setShowMcForm(false);
            onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleDeleteMcEvent = async (eventId) => {
        if (!window.confirm('Supprimer cet evenement ?')) return;
        try {
            await axios.delete(`${API}/main-courante/${eventId}`, { headers });
            toast.success('Evenement supprime');
            onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    return (
        <Card className="content-card" data-testid="main-courante-card">
            <CardHeader className="card-header">
                <div className="flex items-center justify-between w-full">
                    <CardTitle className="card-title flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-cyan-500" />
                        Main courante
                    </CardTitle>
                    {mainCourante.length > 0 && (
                        <a href={`${API}/export/main-courante?token=${token}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-cyan-400 hover:text-cyan-300 text-xs"
                            data-testid="export-main-courante-btn">
                            <FileDown className="w-3.5 h-3.5" /> PDF
                        </a>
                    )}
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <div className="max-h-[360px] overflow-y-auto">
                    {mainCourante.length > 0 ? (
                        <div className="relative pl-6">
                            <div className="absolute left-[11px] top-0 bottom-0 w-px bg-zinc-700"></div>
                            {[...mainCourante].reverse().map((evt) => (
                                <div key={evt.id} className="relative py-3 pr-4 group" data-testid={`mc-event-${evt.id}`}>
                                    <div className={`absolute left-[-13px] top-4 w-2.5 h-2.5 rounded-full ${catColors[evt.category] || catColors.general} ring-2 ring-zinc-900`}></div>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <span className="font-mono text-xs text-cyan-400">
                                                    {new Date(evt.event_time).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                                                </span>
                                                <span className={`text-[9px] px-1.5 py-0.5 rounded ${catColors[evt.category] || catColors.general} bg-opacity-20 text-white`}>
                                                    {catLabels[evt.category] || evt.category}
                                                </span>
                                            </div>
                                            <p className="text-sm text-white">{evt.description}</p>
                                            <p className="text-[10px] text-zinc-600 mt-0.5">par {evt.created_by}</p>
                                        </div>
                                        {isChef && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleDeleteMcEvent(evt.id)}
                                                className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 text-zinc-500 hover:text-red-400"
                                                data-testid={`mc-delete-${evt.id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 text-center text-zinc-500">
                            <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Aucun evenement enregistre</p>
                            <p className="text-xs text-zinc-600 mt-1">Cliquez sur "Saisir" pour ajouter le premier evenement</p>
                        </div>
                    )}
                </div>
                {isChef && (
                    <>
                        {!showMcForm ? (
                            <div className="p-3 border-t border-zinc-800">
                                <Button
                                    size="sm"
                                    onClick={() => setShowMcForm(true)}
                                    className="tactical-btn text-xs w-full"
                                    data-testid="mc-add-event-btn"
                                >
                                    <Plus className="w-3.5 h-3.5 mr-1" />
                                    Saisir un evenement
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleAddMcEvent} className="p-4 border-t border-zinc-800 space-y-3" data-testid="mc-form">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Date/Heure</label>
                                        <input
                                            type="datetime-local"
                                            value={mcForm.event_time}
                                            onChange={(e) => setMcForm({...mcForm, event_time: e.target.value})}
                                            className="w-full mt-1 px-2.5 py-1.5 rounded bg-zinc-800 border border-zinc-700 text-white text-sm"
                                            data-testid="mc-event-time-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Categorie</label>
                                        <select
                                            value={mcForm.category}
                                            onChange={(e) => setMcForm({...mcForm, category: e.target.value})}
                                            className="w-full mt-1 px-2.5 py-1.5 rounded bg-zinc-800 border border-zinc-700 text-white text-sm"
                                            data-testid="mc-category-select"
                                        >
                                            <option value="general">General</option>
                                            <option value="alerte">Alerte</option>
                                            <option value="decision">Decision</option>
                                            <option value="intervention">Intervention</option>
                                            <option value="logistique">Logistique</option>
                                            <option value="communication">Communication</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Description</label>
                                    <input
                                        type="text"
                                        value={mcForm.description}
                                        onChange={(e) => setMcForm({...mcForm, description: e.target.value})}
                                        className="w-full mt-1 px-2.5 py-1.5 rounded bg-zinc-800 border border-zinc-700 text-white text-sm"
                                        placeholder="Ex: Arrivee des pompiers sur site..."
                                        required
                                        data-testid="mc-description-input"
                                    />
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button type="button" variant="ghost" size="sm" onClick={() => setShowMcForm(false)} className="text-xs h-7">Annuler</Button>
                                    <Button type="submit" size="sm" className="tactical-btn text-xs h-7" data-testid="mc-submit-btn">Ajouter</Button>
                                </div>
                            </form>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default MainCouranteCard;
